import { createElement as rc, useRef, useEffect } from 'react';
import useEventSink from '../../hooks/useEventSink';
import Toggle from '../_abstractComponent/Toggle';
import MenuShell from '../menuSelection/MenuShell';
import useFormContext from '../../hooks/useFormContext';
import useActiveRecord from '../../hooks/useActiveRecord';

// @ts-ignore
/** @typedef {import('../../hooks/useFormMachine').FormContext} FormContext */

export function ActiveToggle(props) {
    const activeRecord = useActiveRecord();
    const { hNode = {}, hNode: { namespace: hNamespace, relation: hRelation, type: hType }, actionPayload = {}, ...otherProps } = props;
    const {
        title,
        propertyName = 'active'
    } = hNode;

    const [subscribe, publish] = useEventSink();
    /** @type {FormContext} */
    const formContext = useFormContext() || {};
    const record = actionPayload?.record || formContext.newRecord || {};
    // Submit is disabled when form is not dirty, but we want to allow toggling
    // even when form is not dirty, so we check for submitting state instead
    const disabled = props.disabled || formContext.submitting;
    const isActive = record[propertyName] === true || record[propertyName] === undefined;

    // Use a ref to always get the latest isDirty value in the async callback
    const isDirtyRef = useRef(formContext.isDirty);
    useEffect(() => {
        isDirtyRef.current = formContext.isDirty;
    }, [formContext.isDirty]);

    async function onToggle() {
        if (disabled) return;

        const newValue = !isActive;
        const namespace = hNamespace || activeRecord.namespace;
        const relation = hRelation || activeRecord.relation;
        const type = hType || activeRecord.type;
        const { formInstanceId } = activeRecord;

        // Use the same formInstanceId for both events. The user may open another form
        // while this handler waits for confirmation and change processing.
        // If form already has changes, confirm before proceeding
        if (isDirtyRef.current) {
            const confirmed = await showConfirmation(
                publish,
                'This will save all your changes. Continue?'
            );
            if (!confirmed) return;
        }

        // Wait for the change to be processed before submitting
        const changeProcessed = new Promise(resolve => {
            const unsubscribe = subscribe(
                { verb: 'change', namespace, relation, type, status: 'success' },
                () => {
                    unsubscribe();
                    resolve();
                }
            );
        });

        // Update the active field via beginChange (marks form dirty)
        publish(
            { propertyName, newValue },
            { verb: 'beginChange', namespace, relation, type, formInstanceId }
        );

        // Wait for the change to be processed
        await changeProcessed;

        // Now submit the form to persist the change
        publish(
            { record: { ...record, [propertyName]: newValue }, keepOpenAfterSubmit: true },
            { verb: 'submit', namespace, relation, type, formInstanceId }
        );
    }

    return rc(
        MenuShell,
        { hNode, ...otherProps },
        rc(Toggle, {
            onClick: onToggle,
            title,
            value: isActive,
            disabled,
            falseLabel: '',
            trueLabel: '',
            id: propertyName + (props.id || '')
        })
    );
}

/**
 * Shows a confirmation dialog and returns a promise that resolves to true (confirmed) or false (canceled).
 * @param {Function} publish - The publish function from useEventSink
 * @param {string} message - The confirmation message to display
 * @returns {Promise<boolean>}
 */
function showConfirmation(publish, message) {
    return new Promise(resolve => {
        publish(
            {
                message,
                okButtonText: 'YES',
                cancelButtonText: 'NO',
                okAction: () => resolve(true),
                cancelAction: () => resolve(false)
            },
            { verb: 'confirm', namespace: 'application', relation: 'user' }
        );
    });
}

export default ActiveToggle;
